import React, { useEffect, useState } from "react";
import { Smartphone, X, Share, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/I18nContext";
import { useIsDesktop } from "@/hooks/useMediaQuery";

const KEY = "lp_install_hint_dismissed";

const isStandalone = () =>
  (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
  (typeof navigator !== "undefined" && navigator.standalone === true);

const isIOS = () => typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

/**
 * One-time "Add to home screen" hint for phones/tablets. Hidden once the app runs installed
 * (standalone) or after the user dismisses it. Android/Chrome gets a real Install button
 * (beforeinstallprompt); iOS gets the Share -> Add to Home Screen instructions.
 */
export const InstallHint = () => {
  const { t } = useT();
  const isDesktop = useIsDesktop();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  const [promptEvent, setPromptEvent] = useState(() => window.__lpInstallPrompt || null);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); window.__lpInstallPrompt = e; setPromptEvent(e); };
    const onInstalled = () => { setDismissed(true); try { localStorage.setItem(KEY, "1"); } catch { /* preference only */ } };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  if (isDesktop || dismissed || isStandalone()) return null;

  const dismiss = () => { setDismissed(true); try { localStorage.setItem(KEY, "1"); } catch { /* preference only */ } };
  const install = async () => {
    if (!promptEvent) return;
    try {
      promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      if (choice?.outcome === "accepted") dismiss();
    } catch { /* user closed the browser prompt */ }
    window.__lpInstallPrompt = null;
    setPromptEvent(null);
  };

  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/5 p-3 text-sm" data-testid="install-hint" role="note">
      <div className="mt-0.5 rounded-md bg-primary/10 p-1.5 text-primary"><Smartphone className="h-4 w-4" /></div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{t("pwa.title")}</div>
        {promptEvent ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{t("pwa.androidHint")}</p>
        ) : isIOS() ? (
          <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            {t("pwa.iosStep1")} <Share className="inline h-3.5 w-3.5" /> {t("pwa.iosStep2")}
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-muted-foreground">{t("pwa.genericHint")}</p>
        )}
        {promptEvent && (
          <Button size="sm" className="mt-2 h-8" onClick={install} data-testid="install-hint-install">
            <Download className="mr-1.5 h-4 w-4" /> {t("pwa.install")}
          </Button>
        )}
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={dismiss} aria-label={t("common.close")} data-testid="install-hint-dismiss">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
